/* Fittes Quiz — Vent Finder + Size Guide
 *
 * Two modes, one engine:
 *   overlay     full-screen <dialog> (snippets/fittes-quiz-overlay.liquid) opened from menu links,
 *               PDP triggers or ?quiz=vent-finder on any page
 *   standalone  /pages/vent-finder and /pages/size-guide (layout/quiz.liquid)
 *
 * Product location / finish / material are read from titles (metafields override when set).
 * Answer picks advance automatically with an out/in transition that starts on click;
 * Continue only shows when returning to an answered step.
 * Debug: add ?fq_debug=1 to the URL to log what was read from each product.
 * Analytics go through window.FittesQuizTrack (assets/fittes-quiz-analytics.js).
 * Public API: window.FittesQuiz.open('vent-finder' | 'size-guide', { entry, prefill })
 */
(() => {
  'use strict';
  if (window.FittesQuiz) return;

  /* ------------------------------------------------------------------ helpers */

  const PARAMS = ['quiz', 'loc', 'finish', 'size', 'air', 'mat', 'short', 'long'];
  const SLUG = { vent_finder: 'vent-finder', size_guide: 'size-guide' };
  const KEY = {
    'vent-finder': 'vent_finder', vent_finder: 'vent_finder',
    'size-guide': 'size_guide', size_guide: 'size_guide'
  };
  const LOCATIONS = ['wall', 'ceiling', 'floor', 'exhaust'];
  const LOC_LABEL = { wall: 'Wall', ceiling: 'Ceiling', floor: 'Floor', exhaust: 'Exhaust fan' };
  const locLabel = (l) => LOC_LABEL[l] || cap(l);
  const LEAVE_MS = 160; // exit animation; the next step's enter animation (CSS) runs straight after
  const reducedMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  const FINISHES = ['flush', 'framed'];
  const MATERIALS = ['luxe', 'lite', 'both'];
  const NO_SIZE_BODY = 'Reach out to our support team with your measurements — we often have new sizes in the pipeline, and when there’s enough demand for a size, we add it.';
  const MATERIAL_LABEL = { luxe: 'Luxe', lite: 'Lite', both: 'No preference' };

  const DEFAULT_SHORT = [
    { label: '2¼″ – 2⅝″', n: 2 }, { label: '2⅞″ – 3⅜″', n: 3 }, { label: '3¾″ – 4¼″', n: 4 },
    { label: '5¾″ – 6¼″', n: 6 }, { label: '7¾″ – 8¼″', n: 8 }, { label: '9¾″ – 10¼″', n: 10 },
    { label: '11¾″ – 12¼″', n: 12 }, { label: '14⅛″ – 14⅝″', n: 14 }
  ];
  const DEFAULT_LONG = [
    { label: '9¾″ – 10¼″', n: 10 }, { label: '11¾″ – 12¼″', n: 12 }, { label: '13¾″ – 14¼″', n: 14 }
  ];

  const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
  ));
  const pad2 = (n) => String(n).padStart(2, '0');
  const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : '');
  const asList = (v) => (v == null ? [] : Array.isArray(v) ? v : String(v).split(','))
    .map((s) => String(s).trim().toLowerCase())
    .filter(Boolean);
  const one = (v, allowed) => {
    const list = asList(v);
    return list.length === 1 && (!allowed || allowed.includes(list[0])) ? list[0] : null;
  };
  const track = (fn, ...args) => {
    try { window.FittesQuizTrack?.[fn]?.(...args); } catch (e) { /* analytics must never break the quiz */ }
  };
  const readJSON = (root, sel, fallback) => {
    try { return JSON.parse(root.querySelector(sel)?.textContent || ''); } catch (e) { return fallback; }
  };

  // Sizes are normalised to "4x12" (short × long, inches) whatever the option value looks like
  const normSize = (v) => {
    if (v == null) return null;
    const m = String(v).match(/(\d+(?:\.\d+)?)\D+?(\d+(?:\.\d+)?)/);
    if (!m) return null;
    const [a, b] = [Number(m[1]), Number(m[2])].sort((x, y) => x - y);
    return `${a}x${b}`;
  };
  const sizeNums = (s) => s.split('x').map(Number);
  const fmtSize = (s) => { const [a, b] = sizeNums(s); return `${a}×${b}″`; };
  const fmtSizeLong = (s) => { const [a, b] = sizeNums(s); return `${a}″ × ${b}″`; };
  const FRAC = { 0: '', 0.125: '⅛', 0.25: '¼', 0.375: '⅜', 0.5: '½', 0.625: '⅝', 0.75: '¾', 0.875: '⅞' };
  const inches = (x) => {
    const whole = Math.floor(x);
    const f = Math.round((x - whole) * 8) / 8;
    return `${whole || ''}${f in FRAC ? FRAC[f] : String(f).slice(1)}″`;
  };
  /* Measuring tool: parse what people type ("4 1/8", "4-1/8", "4⅛", "4.125", or cm), match it to a range */
  const FRAC_CHARS = { '⅛': 0.125, '¼': 0.25, '⅜': 0.375, '½': 0.5, '⅝': 0.625, '¾': 0.75, '⅞': 0.875 };
  const parseInchText = (raw) => {
    const t = String(raw || '').trim().replace(/(″|"|inches|inch|in)$/i, '').trim();
    if (!t) return null;
    let m = t.match(/^(\d+(?:\.\d+)?)?\s*([⅛¼⅜½⅝¾⅞])$/);
    if (m) return (m[1] ? Number(m[1]) : 0) + FRAC_CHARS[m[2]];
    m = t.match(/^(\d+)[\s-]+(\d+)\/(\d+)$/);
    if (m && Number(m[3])) return Number(m[1]) + Number(m[2]) / Number(m[3]);
    m = t.match(/^(\d+)\/(\d+)$/);
    if (m && Number(m[2])) return Number(m[1]) / Number(m[2]);
    if (/^\d+(?:\.\d+)?$/.test(t)) return Number(t);
    return null;
  };
  const parseMeasure = (raw, unit) => {
    if (unit === 'cm') {
      const n = parseFloat(String(raw || '').replace(',', '.').replace(/cm$/i, ''));
      return Number.isFinite(n) ? n / 2.54 : null;
    }
    return parseInchText(raw);
  };
  const gcd = (a, b) => (b ? gcd(b, a % b) : a);
  const inchText = (x) => { // 4.125 → "4 1/8" (what the inch field shows)
    let w = Math.floor(x + 1e-9);
    let e = Math.round((x - w) * 8);
    if (e === 8) { w += 1; e = 0; }
    if (!e) return String(w);
    const g = gcd(e, 8);
    return `${w ? `${w} ` : ''}${e / g}/${8 / g}`;
  };
  const cmText = (x) => (x * 2.54).toFixed(1);
  const rangeBounds = (o) => {
    const parts = String(o.label).split(/–|—|-|to/).map((p) => parseInchText(p));
    return parts.length >= 2 && parts[0] != null && parts[1] != null ? [parts[0], parts[1]] : [o.n - 0.25, o.n + 0.25];
  };
  // Inside a range = that size. Within ⅛″ of a range edge = the closest size. Otherwise no match.
  const matchRange = (opts, x) => {
    let best = null;
    let bestGap = Infinity;
    opts.forEach((o) => {
      const gap = x < o.lo ? o.lo - x : x > o.hi ? x - o.hi : 0;
      if (gap < bestGap) { best = o; bestGap = gap; }
    });
    return bestGap <= 0.125 + 1e-9 ? best : null;
  };
  const sortSizes = (arr) => [...new Set(arr.filter(Boolean))].sort((x, y) => {
    const [a1, b1] = sizeNums(x);
    const [a2, b2] = sizeNums(y);
    return a1 - a2 || b1 - b2;
  });
  // Short, clean blurb for the results card: strips markdown-style asterisks, cuts at a word boundary
  const clip = (text, max = 140) => {
    const t = String(text || '').replace(/\*/g, '').replace(/\s+/g, ' ').trim();
    if (t.length <= max) return t;
    const cut = t.slice(0, max);
    return `${cut.slice(0, cut.lastIndexOf(' ')).replace(/[,;:.\-–—\s]+$/, '')}…`;
  };
  // Install wording per product: framed drops in; flush is mudded in, except flush floor vents (subfloor)
  const installLabel = (p) => {
    if (p.finish === 'framed') return 'DIY install (Drop In)';
    if (p.finish === 'flush') return p.location.includes('floor') ? 'Pro install (Subfloor)' : 'Pro install (Mud In)';
    return '';
  };
  const MATERIAL_SPEC = { luxe: 'Powder Coated Steel', lite: 'ABS Plastic' };

  const svg = (w, stroke, body) => `<svg width="${w}" height="${w}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="${stroke}" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${body}</svg>`;
  const ICON = {
    check: svg(18, 2, '<polyline points="5 12.5 10 17 19 7"></polyline>'),
    x: svg(18, 2, '<line x1="6" y1="6" x2="18" y2="18"></line><line x1="18" y1="6" x2="6" y2="18"></line>'),
    checkCream: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#EFF1ED" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="5 12.5 10 17 19 7"></polyline></svg>',
    pencil: svg(14, 1.8, '<path d="M4 20h4L19 9l-4-4L4 16v4z"></path>'),
    bag: svg(20, 1.6, '<path d="M6 7h12l-1 13H7L6 7z"></path><path d="M9 7a3 3 0 0 1 6 0"></path>'),
    close: svg(22, 1.8, '<line x1="6" y1="6" x2="18" y2="18"></line><line x1="18" y1="6" x2="6" y2="18"></line>')
  };

  /* ------------------------------------------------------------------ data */

  const parseRanges = (value, fallback) => {
    try {
      const arr = typeof value === 'string' ? JSON.parse(value) : value;
      const ok = Array.isArray(arr) ? arr.filter((r) => r && r.label && Number.isFinite(Number(r.n))) : [];
      return ok.length ? ok.map((r) => ({ label: String(r.label), n: Number(r.n) })) : fallback;
    } catch (e) {
      return fallback;
    }
  };

  /* Location, finish and material come from the product title.
     A vent.location / vent.finish / vent.material metafield overrides the title when set.
     Airflow: a "+" straight after a word (Luxe+, Lite+), "plus" or "high flow" = Airflow+; otherwise standard.
     Finish must be named in the title ("Flush" / "Framed") or set in the vent.finish metafield. */
  const TITLE_RULES = {
    location: { wall: /\b(side)?wall\b/i, ceiling: /\bceiling\b/i, floor: /\bfloor\b/i },
    finish: { flush: /\bflush\b/i, framed: /\bframed\b/i },
    material: { luxe: /\bluxe\b/i, lite: /\blite\b/i }
  };
  const fromTitle = (title, field) => Object.keys(TITLE_RULES[field]).filter((k) => TITLE_RULES[field][k].test(title));
  const titleFinish = (t) => fromTitle(t, 'finish')[0] || null;
  const titleAirflow = (t) => (/[a-z]\+|\bplus\b|\bhigh[- ]?(air)?flow\b/i.test(t) ? 'plus' : 'standard');

  const normalizeProduct = (p) => {
    const title = String(p.title || '');
    const metaLoc = asList(p.location);
    return {
      ...p,
      location: metaLoc.length ? metaLoc : fromTitle(title, 'location'),
      finish: asList(p.finish)[0] || titleFinish(title),
      material: asList(p.material)[0] || fromTitle(title, 'material')[0] || null,
      airflow: one(p.airflow, ['plus', 'standard']) || titleAirflow(title),
      variants: (p.variants || [])
        .map((v) => ({ id: v.id, price: v.price, available: v.available, rawSize: v.size, size: normSize(v.size) }))
        .filter((v) => v.size)
    };
  };

  // Wall and ceiling vents are interchangeable: picking either shows the same products
  const LOCATION_GROUP = { wall: ['wall', 'ceiling'], ceiling: ['wall', 'ceiling'], floor: ['floor'] };
  // Exhaust mounts only ever match the "Exhaust fan" answer; vents never do
  const matchesLoc = (p, loc) => {
    if (!loc) return true;
    if (loc === 'exhaust') return !!p.exhaust;
    if (p.exhaust) return false;
    return !p.location.length || p.location.some((l) => (LOCATION_GROUP[loc] || [loc]).includes(l));
  };

  // Products the quiz must never recommend, even if they sit in the vent collection
  const EXCLUDE_TITLES = /\bniche\b|\breceptacle\b|\bdevice mount\b/i;
  // Exhaust mounts: shown by the Size Guide, and by the Vent Finder only for "Exhaust fan"
  const EXHAUST_TITLES = /\bexhaust\b/i;

  class QuizData {
    constructor(root) {
      this.config = readJSON(root, '[data-fq-config]', {});
      this.config.urls = this.config.urls || {};
      this.config.images = this.config.images || {};
      this.config.flags = this.config.flags || {};
      this.config.copy = this.config.copy || {};
      this.products = readJSON(root, '[data-fq-index]', [])
        .filter((p) => !EXCLUDE_TITLES.test(String(p.title || '')))
        .map(normalizeProduct)
        .map((p) => ({ ...p, exhaust: EXHAUST_TITLES.test(String(p.title || '')) }))
        .filter((p) => p.variants.length);
      this.shortRanges = parseRanges(this.config.shortRanges, DEFAULT_SHORT);
      this.longRanges = parseRanges(this.config.longRanges, DEFAULT_LONG);
      this.allSizes = sortSizes(this.products.flatMap((p) => p.variants.map((v) => v.size)));
      this.fallbackSizes = sortSizes(String(this.config.fallbackSizes || '').split(/[\s,]+/).map(normSize));
      if (!this.products.length) console.warn('[FittesQuiz] No products with sizes found. Check the section collection and size option name.');
      if (/[?&]fq_debug=1/.test(window.location.search)) {
        console.table(this.products.map((p) => ({
          title: p.title,
          location: p.location.join(', ') || '(any)',
          finish: p.finish || '— missing',
          material: p.material || '— missing',
          airflow: p.airflow,
          sizes: [...new Set(p.variants.map((v) => v.size))].join(' ')
        })));
      }
    }

    money(cents) {
      try {
        return new Intl.NumberFormat(this.config.locale || 'en', {
          style: 'currency', currency: this.config.currency || 'USD'
        }).format(cents / 100);
      } catch (e) {
        return `$${(cents / 100).toFixed(2)}`;
      }
    }

    variantFor(p, size) {
      return p.variants.find((v) => v.size === size && v.available) || p.variants.find((v) => v.size === size) || null;
    }

    rawSizeFor(size) {
      for (const p of this.products) {
        const v = p.variants.find((x) => x.size === size);
        if (v) return v.rawSize;
      }
      return size;
    }

    // Range label for a nominal inch value: the configured label if one exists, else n−¼″ to n+¼″
    rangeLabel(n, side = 'short') {
      const [own, other] = side === 'long' ? [this.longRanges, this.shortRanges] : [this.shortRanges, this.longRanges];
      const cfg = own.find((r) => r.n === n) || other.find((r) => r.n === n);
      return cfg ? cfg.label : `${inches(n - 0.25)} – ${inches(n + 0.25)}`;
    }

    // Short-side options = every short side in the catalog, so each pick leads to real products
    shortOptions() {
      if (!this.allSizes.length) return this.shortRanges;
      const ns = [...new Set(this.allSizes.map((s) => sizeNums(s)[0]))].sort((a, b) => a - b);
      return ns.map((n) => ({ n, label: this.rangeLabel(n) }));
    }

    // Long-side options = every long side the catalog pairs with that short side (e.g. 8 → 8, 10, 12, 14, 30)
    longOptions(shortN) {
      if (!this.allSizes.length) return this.longRanges.filter((r) => r.n >= shortN);
      const ns = [...new Set(this.allSizes.map(sizeNums).filter(([a]) => a === shortN).map(([, b]) => b))].sort((a, b) => a - b);
      return ns.map((n) => ({ n, label: this.rangeLabel(n, 'long') }));
    }

    // Size Guide shows everything sold in a size (any location, finish or product type in the collection)
    productsWithSize(size) {
      return this.products
        .filter((p) => this.variantFor(p, size))
        .sort((a, b) => Number(this.variantFor(b, size).available) - Number(this.variantFor(a, size).available));
    }

    sizesFor(location, finish) {
      if (!this.products.length) return this.fallbackSizes;
      return sortSizes(this.products
        .filter((p) => matchesLoc(p, location) && (!finish || p.finish === finish))
        .flatMap((p) => p.variants.map((v) => v.size)));
    }
  }

  /* ------------------------------------------------------------------ view parts */

  const V = {
    btn(label, { variant = 'primary', size = '', action = '', attrs = '', disabled = false, block = false } = {}) {
      const cls = ['fq-btn', `fq-btn--${variant}`, size && `fq-btn--${size}`, block && 'fq-btn--block'].filter(Boolean).join(' ');
      return `<button type="button" class="${cls}"${action ? ` data-action="${action}"` : ''} ${attrs}${disabled ? ' disabled' : ''}>${label}</button>`;
    },
    badge: (text, kind = 'solid', size = 'sm') => `<span class="fq-badge fq-badge--${kind} fq-badge--${size}">${esc(text)}</span>`,
    tip: (kind, text) => `<div class="fq-tip fq-tip--${kind}">${kind === 'do' ? ICON.check : ICON.x}<span>${esc(text)}</span></div>`,
    spec(rows) {
      const html = rows.filter(([, v]) => v).map(([k, v]) => `<span class="fq-spec__row"><span class="fq-spec__k">${esc(k)}</span><span class="fq-spec__v">${esc(v)}</span></span>`).join('');
      return html ? `<span class="fq-spec">${html}</span>` : '';
    },
    group: (q, label, inner, cls) => `<div class="${cls}" role="radiogroup" aria-label="${esc(label)}" data-q="${q}">${inner}</div>`,
    radioAttrs: (v, selected) => `role="radio" aria-checked="${selected}" data-action="pick" data-v="${esc(v)}"`,
    media(src, cls, alt = '', badge = '') {
      return `<div class="fq-media ${cls}">${src ? `<img src="${esc(src)}" alt="${esc(alt)}" loading="lazy">` : ''}${badge ? `<span class="fq-media__badge">${badge}</span>` : ''}</div>`;
    },
    question({ num, name, title, lead, extra = '', answers }) {
      return `<section class="fq-q">
        <div class="fq-q__intro">
          <span class="fq-eyebrow fq-eyebrow--red">${pad2(num)} — ${esc(name)}</span>
          <h1 class="fq-h1" id="fq-title" tabindex="-1">${title}</h1>
          <p class="fq-lead">${lead}</p>
          ${extra}
        </div>
        <div class="fq-q__answers">${answers}</div>
      </section>`;
    },
    option({ v, title, caption, img, ratio, selected, badge, desc, specs }) {
      return `<button type="button" class="fq-option${selected ? ' is-selected' : ''}" ${V.radioAttrs(v, selected)}>
        <span class="fq-option__media" style="aspect-ratio:${ratio}">
          ${img ? `<img src="${esc(img)}" alt="" loading="lazy">` : ''}
          ${badge ? `<span class="fq-option__badge">${V.badge(badge)}</span>` : ''}
          <span class="fq-option__check">${ICON.checkCream}</span>
        </span>
        <span class="fq-option__body">
          <span class="fq-option__title">${esc(title)}</span>
          ${caption ? `<span class="fq-option__caption">${esc(caption)}</span>` : ''}
          ${desc ? `<span class="fq-option__desc">${esc(desc)}</span>` : ''}
          ${specs ? V.spec(specs) : ''}
        </span>
      </button>`;
    },
    tile: ({ v, label, n, selected }) => `<button type="button" class="fq-radio${selected ? ' is-selected' : ''}" ${V.radioAttrs(v, selected)}>
        <span class="fq-radio__circle"></span>
        <span class="fq-radio__label">${esc(label)}</span>
        <span class="fq-radio__n">${esc(n)}″</span>
      </button>`,
    addButton(app, quiz, p, v, { qty = 1, variant = 'secondary', size = '' } = {}) {
      if (!v) return '';
      const added = quiz.added[v.id];
      const attrs = `data-variant="${v.id}" data-qty="${qty}" data-name="${esc(p.title)}" data-price="${v.price}" data-focus="add-${v.id}"`;
      if (!v.available) return V.btn('Sold out', { variant, size, block: true, disabled: true });
      if (added) return V.btn('Added to cart', { variant: 'ink', size, block: true, action: 'add', attrs });
      const label = variant === 'primary' ? `Add to cart — ${app.data.money(v.price * qty)}` : 'Add to cart';
      return V.btn(label, { variant, size, block: true, action: 'add', attrs });
    },
    card(app, quiz, p, size) {
      const v = app.data.variantFor(p, size);
      const url = `${p.url}?variant=${v.id}`;
      const meta = [fmtSize(size), installLabel(p), p.airflow === 'plus' && 'Airflow+'].filter(Boolean).join(' · ');
      return `<article class="fq-card">
        <a class="fq-card__media" href="${esc(url)}" data-action="pdp" tabindex="-1" aria-hidden="true">${p.image ? `<img src="${esc(p.image)}" alt="" loading="lazy">` : ''}</a>
        <div class="fq-card__body">
          <h3 class="fq-card__title"><a href="${esc(url)}" data-action="pdp">${esc(p.title)}</a></h3>
          <p class="fq-card__meta">${esc(meta)}</p>
          <p class="fq-card__price">${app.data.money(v.price)}</p>
        </div>
        <div class="fq-card__buy">${V.addButton(app, quiz, p, v)}</div>
      </article>`;
    },
    promo({ eyebrow, title, body, cta }) {
      return `<div class="fq-promo fq-grid-dark">
        <div class="fq-promo__text">
          <span class="fq-eyebrow">${esc(eyebrow)}</span>
          <h3 class="fq-promo__h3">${title}</h3>
          <p class="fq-promo__body">${esc(body)}</p>
        </div>
        ${cta}
      </div>`;
    },
    // Inline version of the no-size message (used where a step has nothing to offer)
    noSize(app) {
      return `<div class="fq-nosize" role="status">
        <p class="fq-nosize__title">Our current inventory isn’t sized to fit your needs <span class="fq-em">yet.</span></p>
        <p class="fq-nosize__body">${esc(NO_SIZE_BODY)}</p>
        <div class="fq-actions">
          ${V.btn('Chat with us', { variant: 'primary', action: 'chat' })}
          ${V.btn('Get help with my size', { variant: 'secondary', action: 'help', attrs: 'data-kind="no_size"' })}
        </div>
      </div>`;
    },
    help(app, kind, againLabel) {
      const c = app.data.config;
      const copy = {
        no_size: ['Not in our range yet', `Our current inventory isn’t sized to fit your needs. ${NO_SIZE_BODY}`],
        none: ['Not in our range yet', `Our current inventory isn’t sized to fit your needs. ${NO_SIZE_BODY}`],
        round: ['Round duct', 'Round ducts need a quick adapter conversation. Send us your diameter and a photo of the opening — our team will match you with a Fittes vent that fits.'],
        no_match: ['Not in our range yet', `Our current inventory isn’t sized to fit your needs. ${NO_SIZE_BODY}`]
      }[kind] || [];
      const body = `${app.quiz.summary()}\n\nMy exact measurements:\n\n(Please attach a photo of the opening.)`;
      const emailHref = c.supportEmail
        ? `mailto:${c.supportEmail}?subject=${encodeURIComponent('Help finding my vent size')}&body=${encodeURIComponent(body)}`
        : c.urls.contact;
      return `<section class="fq-help fq-grid-cream">
        <div class="fq-help__inner">
          <span class="fq-eyebrow fq-eyebrow--red">${esc(copy[0])}</span>
          <h1 class="fq-help__h1" id="fq-title" tabindex="-1">Let’s get you the right <span class="fq-em">fit.</span></h1>
          <p class="fq-lead">${esc(copy[1])}</p>
          <div class="fq-actions">
            ${V.btn('Chat with us', { variant: 'primary', size: 'lg', action: 'chat' })}
            ${emailHref ? `<a class="fq-btn fq-btn--secondary fq-btn--lg" href="${esc(emailHref)}" data-action="email">Email our team</a>` : ''}
          </div>
          <div class="fq-help__again">
            <span>Think you mis-measured? It happens.</span>
            ${V.btn(againLabel, { variant: 'ghost', action: 'again' })}
          </div>
        </div>
      </section>`;
    }
  };

  /* ------------------------------------------------------------------ Vent Finder */

  const AIRFLOWS = ['plus', 'standard', 'any'];
  const AIRFLOW_LABEL = { plus: 'Airflow+', standard: 'Standard airflow', any: 'Either airflow' };

  class VentFinder {
    constructor(app, pre = {}) {
      this.app = app;
      this.id = 'vent_finder';
      this.label = 'Vent Finder';
      this.total = 5;
      this.keys = ['location', 'finish', 'size', 'airflow', 'material'];
      this.help = null;
      this.added = {};
      this.results = null;
      this.editing = false;
      this.s = {
        step: 0,
        qty: 1,
        location: one(pre.location, LOCATIONS),
        finish: one(pre.finish, FINISHES),
        size: normSize(pre.size),
        airflow: one(pre.airflow, AIRFLOWS),
        material: one(pre.material, MATERIALS)
      };
      if (this.s.size && this.s.location && this.s.finish && !this.availableSizes().includes(this.s.size)) this.s.size = null;

      const firstMissing = this.keys.findIndex((k) => this.s[k] == null);
      if (Number.isInteger(pre.step)) this.s.step = Math.min(pre.step, firstMissing === -1 ? 4 : firstMissing);
      else if (pre.fromQuery && firstMissing === -1) this.computeResults(); // shareable results link
    }

    // Word used in copy ("measure the duct opening in the …"); exhaust fans sit in the ceiling
    get surface() { return this.s.location === 'exhaust' ? 'ceiling' : this.s.location || 'wall'; }
    availableSizes() { return this.app.data.sizesFor(this.s.location, this.s.finish); }

    isQuestion() { return this.s.step < 5 && !this.help; }
    stepId() { return this.help ? 'help' : this.keys[this.s.step] || 'results'; }
    stepNum() { return this.s.step + 1; }
    canContinue() { return this.s[this.keys[this.s.step]] != null; }
    canBack() { return this.s.step > 0; }
    ctaLabel() { return this.s.step === 4 ? 'See my matches' : 'Continue'; }

    trail() {
      const { location, finish, size, airflow, material } = this.s;
      const parts = [
        location && locLabel(location), cap(finish), size && fmtSize(size),
        airflow && AIRFLOW_LABEL[airflow], material && MATERIAL_LABEL[material]
      ].filter(Boolean);
      return parts.length ? parts.join('  /  ') : 'Pick one to continue';
    }

    set(q, v) {
      this.s[q] = v;
      if ((q === 'location' || q === 'finish') && this.s.size && !this.availableSizes().includes(this.s.size)) this.s.size = null;
    }

    next() {
      // Editing from the results chips: jump straight back to results once everything is answered
      const allAnswered = this.keys.every((k) => this.s[k] != null);
      if (this.s.step < 4 && !(this.editing && allAnswered)) { this.s.step += 1; return; }
      this.editing = false;
      if (this.computeResults()) this.app.trackComplete();
    }

    back() { if (this.s.step > 0) this.s.step -= 1; }

    computeResults() {
      const { location, finish, size, airflow, material } = this.s;
      const data = this.app.data;
      const tier = material === 'both' ? 'luxe' : material;
      // Primary = finish + airflow + build match. Alternates relax build, then airflow, then finish.
      const score = (p) => (p.finish === finish ? 4 : 0)
        + (airflow !== 'any' && p.airflow === airflow ? 2 : 0)
        + (p.material === tier ? 1 : 0);
      const pool = data.products
        .filter((p) => p.finish && matchesLoc(p, location) && data.variantFor(p, size))
        .sort((a, b) => score(b) - score(a));
      if (!pool.length) {
        this.results = null;
        this.help = 'no_match';
        return false;
      }
      this.results = { primary: pool[0], alternates: pool.slice(1, 4) };
      this.help = null;
      this.s.step = 5;
      return true;
    }

    answers() {
      const { location, finish, size, airflow, material } = this.s;
      return { location, finish, size, airflow, material };
    }
    resultKey() { return this.results?.primary?.handle || this.s.size || ''; }
    snapshot() { return this.answers(); }
    query() {
      return { loc: this.s.location, finish: this.s.finish, size: this.s.size, air: this.s.airflow, mat: this.s.material };
    }
    summary() {
      const { location, finish, size, airflow, material } = this.s;
      return `Vent Finder answers — Location: ${location ? locLabel(location) : '–'} / Finish: ${cap(finish) || '–'} / Size: ${size ? fmtSize(size) : '–'} / Airflow: ${AIRFLOW_LABEL[airflow] || '–'} / Build: ${MATERIAL_LABEL[material] || '–'}`;
    }

    action(a, t) {
      const app = this.app;
      switch (a) {
        case 'edit':
          app.go(() => {
            this.help = null;
            this.editing = true;
            this.s.step = Number(t.dataset.step) || 0;
          }, 'back');
          break;
        case 'restart':
          app.go(() => {
            Object.assign(this.s, { step: 0, qty: 1, location: null, finish: null, size: null, airflow: null, material: null });
            this.help = null;
            this.results = null;
            this.editing = false;
          }, 'back');
          break;
        case 'again':
          app.go(() => {
            this.help = null;
            this.s.step = 2;
          }, 'back');
          break;
        case 'help':
          app.go(() => { this.help = t.dataset.kind || 'no_size'; });
          break;
        case 'qty':
          this.s.qty = Math.min(99, Math.max(1, this.s.qty + Number(t.dataset.d)));
          app.focusKey = t.dataset.focus;
          app.render({ stepChanged: false });
          break;
        case 'add':
          app.addToCart(t);
          break;
        default:
      }
    }

    view() {
      if (this.help) return V.help(this.app, this.help, 'Measure again');
      return [this.viewLocation, this.viewFinish, this.viewSize, this.viewAirflow, this.viewMaterial, this.viewResults][this.s.step].call(this);
    }

    viewLocation() {
      const img = this.app.data.config.images;
      const opts = [
        { v: 'wall', title: 'Wall', caption: 'Sidewall, above or below the trim' },
        { v: 'ceiling', title: 'Ceiling', caption: 'Overhead, set into drywall' },
        { v: 'floor', title: 'Floor', caption: 'Underfoot, in any flooring' },
        { v: 'exhaust', title: 'Exhaust fan', caption: 'Over a bathroom or ceiling exhaust fan' }
      ];
      return V.question({
        num: 1,
        name: 'Location',
        title: 'Where does your vent <span class="fq-em">live?</span>',
        lead: 'Every Fittes vent is engineered for a specific surface. Start here and we’ll narrow it down in five quick steps.',
        answers: V.group('location', 'Vent location', opts.map((o) => V.option({
          ...o, img: img[o.v], ratio: '1 / 1', selected: this.s.location === o.v
        })).join(''), 'fq-options fq-options--loc')
      });
    }

    viewFinish() {
      const c = this.app.data.config;
      const opts = [
        {
          v: 'flush', title: 'Flush', badge: 'Pro install recommended',
          desc: 'Sits perfectly level with the surface for a seamless, built-in look.',
          specs: [['Install', 'Mudded into drywall'], ['Best for', 'New builds & renos']]
        },
        {
          v: 'framed', title: 'Framed', badge: 'DIY friendly',
          desc: 'A slim trim edge covers the opening. Drops right into your existing hole.',
          specs: [['Install', c.copy.framedInstall || 'DIY install. No mud, no mess.'], ['Best for', 'Quick upgrades & rentals']]
        }
      ];
      return V.question({
        num: 2,
        name: 'Finish',
        title: 'Flush or <span class="fq-em">framed?</span>',
        lead: `Same clean, modern look. The difference is how it meets your ${esc(this.surface)} — and who’s doing the install.`,
        answers: V.group('finish', 'Finish', opts.map((o) => V.option({
          ...o, img: c.images[o.v], ratio: '4 / 3', selected: this.s.finish === o.v
        })).join(''), 'fq-options fq-options--finish')
      });
    }

    viewSize() {
      const c = this.app.data.config;
      const sizes = this.availableSizes();
      let picker;
      if (!sizes.length) {
        picker = V.noSize(this.app);
      } else if (c.flags.sizeInput === 'dropdown') {
        picker = `<select class="fq-select" data-fq-select="size" aria-label="Duct opening size">
          <option value="">Select duct dimensions</option>
          ${sizes.map((s) => `<option value="${s}"${this.s.size === s ? ' selected' : ''}>${fmtSize(s)}</option>`).join('')}
        </select>`;
      } else {
        picker = V.group('size', 'Duct opening size', sizes.map((s) => {
          const sel = this.s.size === s;
          return `<button type="button" class="fq-chip${sel ? ' is-selected' : ''}" ${V.radioAttrs(s, sel)}>${fmtSize(s)}</button>`;
        }).join(''), 'fq-chips');
      }
      return V.question({
        num: 3,
        name: 'Size',
        title: 'Measure the <span class="fq-em">hole,</span> not the vent.',
        lead: 'Pull off your old cover and measure the duct opening — width × length, in inches. Round to the nearest standard size.',
        extra: `<div class="fq-tips">${V.tip('do', `Do measure the duct opening in the ${this.surface}`)}${V.tip('dont', 'Don’t measure the old vent cover')}</div>`,
        answers: `${V.media(c.images.measure, 'fq-media--photo', 'Removing a vent cover to measure the duct opening')}
          <div class="fq-labelrow">
            <span class="fq-eyebrow fq-eyebrow--muted">Duct opening · W × L (in)</span>
            <button type="button" class="fq-link" data-action="goto-size_guide">Not sure? Use the size guide</button>
          </div>
          ${picker}
          <p class="fq-caption">Don’t see your size? <button type="button" class="fq-link" data-action="help" data-kind="no_size">Let us know what you need</button> — new sizes are often in the pipeline.</p>`
      });
    }

    viewAirflow() {
      const rows = [
        { v: 'plus', title: 'Airflow+', sub: 'More air through the same opening', desc: 'For rooms that run hot, cold or stuffy, and vents at the end of a long duct run.', badges: ['More airflow'] },
        { v: 'standard', title: 'Standard airflow', sub: 'Matches a typical vent', desc: 'Right for most rooms with normal heating and cooling.', badges: [] },
        { v: 'any', title: 'Not sure', sub: 'Show me both', desc: 'We’ll match everything else and show both options side by side.', badges: [] }
      ];
      return V.question({
        num: 4,
        name: 'Airflow',
        title: 'How much <span class="fq-em">air</span> do you need?',
        lead: `Airflow+ vents push more air through the same ${esc(this.surface)} opening. Standard airflow suits most rooms.`,
        answers: V.group('airflow', 'Airflow', this.radioRows(rows, this.s.airflow), 'fq-radios')
      });
    }

    viewMaterial() {
      const rows = [
        { v: 'luxe', title: '[Luxe]', sub: 'Powder-coated solid steel', desc: 'Our premium build. Ultra-durable, sustainable, and made to outlast the house.', badges: ['Solid steel', 'Powder-coated', 'Sustainable'], price: '$$$' },
        { v: 'lite', title: '[Lite]', sub: 'High-quality ABS', desc: 'Lightweight and budget-conscious — still built to last.', badges: ['Lightweight', 'Budget-friendly', 'Durable'], price: '$$' },
        { v: 'both', title: 'No preference', sub: 'Show me both', desc: 'I’ll compare side by side before I decide.', badges: [], price: '' }
      ];
      return V.question({
        num: 5,
        name: 'Material',
        title: 'Choose your <span class="fq-em">build.</span>',
        lead: `Both look identical on the ${esc(this.surface)}. The difference is what they’re made of — and what they cost.`,
        answers: V.group('material', 'Material', this.radioRows(rows, this.s.material), 'fq-radios')
      });
    }

    radioRows(rows, current) {
      return rows.map((r) => {
        const sel = current === r.v;
        return `<button type="button" class="fq-radio fq-radio--large${sel ? ' is-selected' : ''}" ${V.radioAttrs(r.v, sel)}>
          <span class="fq-radio__circle"></span>
          <span class="fq-radio__content">
            <span class="fq-radio__head"><span class="fq-radio__title">${esc(r.title)}</span><span class="fq-radio__sub">${esc(r.sub)}</span></span>
            <span class="fq-radio__desc">${esc(r.desc)}</span>
            ${r.badges.length ? `<span class="fq-radio__badges">${r.badges.map((b) => V.badge(b, 'soft')).join('')}</span>` : ''}
          </span>
          ${r.price ? `<span class="fq-radio__price" aria-label="Relative price ${r.price.length} of 3">${r.price}</span>` : ''}
        </button>`;
      }).join('');
    }

    viewResults() {
      const app = this.app;
      const d = app.data;
      const c = d.config;
      const { primary, alternates } = this.results;
      const { location, finish, size, airflow, material, qty } = this.s;
      const v = d.variantFor(primary, size);

      const chips = [
        ['Location', locLabel(location), 0], ['Finish', cap(finish), 1], ['Size', fmtSize(size), 2],
        ['Airflow', AIRFLOW_LABEL[airflow], 3], ['Build', MATERIAL_LABEL[material], 4]
      ].map(([k, val, step]) => `<button type="button" class="fq-answer-chip" data-action="edit" data-step="${step}" aria-label="Edit ${k.toLowerCase()}: ${esc(val)}">
          <span class="fq-answer-chip__k">${k}</span><span>${esc(val)}</span>${ICON.pencil}
        </button>`).join('');

      const pUrl = `${primary.url}?variant=${v.id}`;
      const eyebrow = [locLabel(location), cap(primary.finish || finish), fmtSize(size)].join(' · ');
      const highlight = primary.highlight; // airflow already shows in the spec list and on the product photo

      const match = `<section class="fq-wrap">
        <article class="fq-match">
          <div class="fq-match__media">
            ${primary.image ? `<img src="${esc(primary.image)}" alt="${esc(primary.title)}">` : ''}
            <div class="fq-match__badges">${V.badge('Best match', 'solid', 'md')}${highlight ? V.badge(highlight, 'soft', 'md') : ''}</div>
          </div>
          <div class="fq-match__body">
            <span class="fq-eyebrow fq-eyebrow--muted">${esc(eyebrow)}</span>
            <h2 class="fq-match__title">${esc(primary.title)}</h2>
            <p class="fq-price">${d.money(v.price)}<span class="fq-price__cur">${esc(c.currency || '')}</span></p>
            ${primary.description ? `<p class="fq-body fq-match__desc">${esc(clip(primary.description))}</p>` : ''}
            ${V.spec([
              ['Duct size', fmtSizeLong(size)],
              ['Airflow', primary.airflow ? AIRFLOW_LABEL[primary.airflow] : ''],
              ['Material', primary.materialLabel || MATERIAL_SPEC[primary.material] || cap(primary.material)],
              ['Install', installLabel(primary)],
              ['Made in', primary.madeIn]
            ])}
            <div class="fq-buy">
              <div class="fq-qty" role="group" aria-label="Quantity">
                <button type="button" data-action="qty" data-d="-1" data-focus="qty-dec" aria-label="Decrease quantity">−</button>
                <span class="fq-qty__n" aria-live="polite">${qty}</span>
                <button type="button" data-action="qty" data-d="1" data-focus="qty-inc" aria-label="Increase quantity">+</button>
              </div>
              ${V.addButton(app, this, primary, v, { qty, variant: 'primary', size: 'lg' })}
            </div>
            <a class="fq-link" href="${esc(pUrl)}" data-action="pdp">View full product details</a>
          </div>
        </article>
      </section>`;

      const alts = alternates.length ? `<section class="fq-wrap">
        <div class="fq-section-head"><h2 class="fq-h2">Also a <span class="fq-em">fit</span></h2><p>Same size, different finish, airflow or build.</p></div>
        <div class="fq-cards">${alternates.map((p) => V.card(app, this, p, size)).join('')}</div>
      </section>` : '';

      const trades = c.flags.showTrades !== false ? `<section class="fq-wrap">${V.promo({
        eyebrow: 'Fittes Trades Program',
        title: 'Installing for a <span class="fq-em">client?</span>',
        body: 'Contractors, designers and builders get trade pricing and dedicated support on every order.',
        cta: `<a class="fq-btn fq-btn--inverse fq-btn--lg" href="${esc(c.urls.trades)}" data-action="pdp">Apply for trade pricing</a>`
      })}</section>` : '';

      return `<section class="fq-hero fq-grid-red">
          <div class="fq-hero__inner">
            <span class="fq-eyebrow">Your match is in</span>
            <h1 class="fq-hero__h1" id="fq-title" tabindex="-1">Your perfect <span class="fq-em">Fit(tes).</span></h1>
            <p class="fq-hero__lead">Built for your ${esc(this.surface)}, sized to your duct. Small detail. Huge impact.</p>
            <div class="fq-answers">${chips}</div>
          </div>
        </section>
        ${match}${alts}${trades}
        <div class="fq-end">${V.btn('Start over', { variant: 'ghost', action: 'restart' })}</div>`;
    }
  }

  /* ------------------------------------------------------------------ Size Guide */

  class SizeGuide {
    constructor(app, pre = {}, returnTo = null) {
      this.app = app;
      this.id = 'size_guide';
      this.label = 'Size Guide';
      this.total = 3;
      this.keys = ['prep', 'short', 'long'];
      this.help = null;
      this.added = {};
      this.returnTo = returnTo;
      this.s = { step: 0, short: null, long: null, unit: 'in', measure: { short: null, long: null } };

      // State holds nominal inches (e.g. short: 8, long: 30)
      this.shorts = app.data.shortOptions();
      const sh = this.shorts.find((o) => String(o.n) === String(pre.short));
      if (sh) {
        this.s.short = sh.n;
        this.s.step = 2;
        const lo = this.longOptions().find((o) => String(o.n) === String(pre.long));
        if (lo) {
          this.s.long = lo.n;
          if (pre.fromQuery) this.s.step = 3; // shareable result link
        }
      }
    }

    get short() { return this.shorts.find((o) => o.n === this.s.short) || null; }
    get long() { return this.longOptions().find((o) => o.n === this.s.long) || null; }

    longOptions() {
      return this.s.short == null ? [] : this.app.data.longOptions(this.s.short);
    }

    sizeKey() { return this.short && this.long ? normSize(`${this.short.n}x${this.long.n}`) : null; }

    isQuestion() { return this.s.step < 3 && !this.help; }
    stepId() { return this.help ? 'help' : this.keys[this.s.step] || 'result'; }
    stepNum() { return this.s.step + 1; }
    canContinue() { return [true, this.s.short != null, this.s.long != null][this.s.step] ?? false; }
    canBack() { return this.s.step > 0; }
    ctaLabel() { return ['Vent is off', 'Continue', 'Get my size'][this.s.step]; }

    trail() {
      if (this.s.step === 0) return 'Measuring the duct, not the vent';
      const parts = [this.short && `Short ${this.short.label}`, this.long && `Long ${this.long.label}`].filter(Boolean);
      return parts.length ? parts.join('  /  ') : 'Pick a range to continue';
    }

    // Picking a range tile directly replaces any typed/slid measurement for that side
    set(q, v) {
      const n = Number(v);
      if (q === 'short' && n !== this.s.short) { this.s.long = null; this.s.measure.long = null; }
      this.s[q] = n;
      this.s.measure[q] = null;
    }

    boundsFor(side) {
      const opts = side === 'short' ? this.shorts : this.longOptions();
      return opts.map((o) => { const [lo, hi] = rangeBounds(o); return { ...o, lo, hi }; });
    }

    measureText(x) { return this.s.unit === 'cm' ? cmText(x) : inchText(x); }

    measureNote(side) {
      const x = this.s.measure[side];
      if (x == null) return 'Drag the slider, type your measurement, or pick a range below.';
      const opt = this.boundsFor(side).find((o) => o.n === this.s[side]);
      if (opt) return `Matches <strong>${esc(opt.label)}</strong>. Continue when you’re ready.`;
      return `No size in our range fits ${esc(inches(x))}. <button type="button" class="fq-link" data-action="help" data-kind="no_size">See your options</button>`;
    }

    // Slider (⅛″ steps) + inch/cm field, shown above the range tiles on each measuring step
    measureHTML(side) {
      const opts = this.boundsFor(side);
      if (!opts.length) return '';
      const min = Math.max(0.5, Math.floor(Math.min(...opts.map((o) => o.lo))) - 1);
      const max = Math.ceil(Math.max(...opts.map((o) => o.hi))) + (side === 'long' ? 2 : 1);
      const x = this.s.measure[side];
      const val = x ?? opts.find((o) => o.n === this.s[side])?.n ?? opts[0].n;
      const fill = ((Math.min(Math.max(val, min), max) - min) / (max - min)) * 100;
      const u = this.s.unit;
      return `<div class="fq-measure" data-fq-measure="${side}" data-min="${min}" data-max="${max}">
          <div class="fq-measure__head">
            <span class="fq-eyebrow fq-eyebrow--muted">Your ${side} side</span>
            <span class="fq-measure__readout" data-fq-readout>${x != null ? `${esc(inches(x))} · ${cmText(x)} cm` : ''}</span>
          </div>
          <div class="fq-measure__row">
            <input type="range" class="fq-range" min="${min}" max="${max}" step="0.125" value="${val}" style="--fq-fill:${fill}%"
              aria-label="${side} side measurement in inches" aria-valuetext="${esc(inches(val))}" data-fq-range>
            <div class="fq-measure__input">
              <input type="text" inputmode="decimal" autocomplete="off" class="fq-measure__text" data-fq-text
                value="${x != null ? esc(this.measureText(x)) : ''}" placeholder="${u === 'cm' ? 'e.g. 10.5' : 'e.g. 4 1/8'}"
                aria-label="${side} side measurement in ${u === 'cm' ? 'centimetres' : 'inches'}">
              <div class="fq-unit" role="group" aria-label="Unit">
                <button type="button" class="fq-unit__btn" data-action="unit" data-unit="in" aria-pressed="${u === 'in'}">in</button>
                <button type="button" class="fq-unit__btn" data-action="unit" data-unit="cm" aria-pressed="${u === 'cm'}">cm</button>
              </div>
            </div>
          </div>
          <p class="fq-measure__note" data-fq-note aria-live="polite">${this.measureNote(side)}</p>
        </div>
        <div class="fq-or" aria-hidden="true"><span>or pick your range</span></div>`;
    }

    onInput(e) {
      const box = e.target.closest('[data-fq-measure]');
      if (!box) return;
      const side = box.dataset.fqMeasure;
      if (e.target.matches('[data-fq-range]')) {
        this.applyMeasure(side, Number(e.target.value), 'range');
      } else if (e.target.matches('[data-fq-text]')) {
        const x = parseMeasure(e.target.value, this.s.unit);
        if (x != null && x > 0) this.applyMeasure(side, x, 'text');
      }
    }

    applyMeasure(side, x, source) {
      const opt = matchRange(this.boundsFor(side), x);
      const v = opt ? opt.n : null;
      if (side === 'short' && v !== this.s.short) { this.s.long = null; this.s.measure.long = null; }
      this.s[side] = v;
      this.s.measure[side] = x;
      this.syncMeasure(side, source);
    }

    // Update the measuring tool, tiles and footer in place (no re-render, so dragging stays smooth)
    syncMeasure(side, source) {
      const el = this.app.el;
      const box = el.querySelector(`[data-fq-measure="${side}"]`);
      if (!box) return;
      const x = this.s.measure[side];
      const min = Number(box.dataset.min);
      const max = Number(box.dataset.max);
      const range = box.querySelector('[data-fq-range]');
      const text = box.querySelector('[data-fq-text]');
      if (x != null) {
        const clamped = Math.min(Math.max(x, min), max);
        if (source !== 'range') range.value = String(clamped);
        range.style.setProperty('--fq-fill', `${((clamped - min) / (max - min)) * 100}%`);
        range.setAttribute('aria-valuetext', inches(x));
        if (source !== 'text') text.value = this.measureText(x);
        box.querySelector('[data-fq-readout]').textContent = `${inches(x)} · ${cmText(x)} cm`;
      }
      box.querySelector('[data-fq-note]').innerHTML = this.measureNote(side);
      el.querySelectorAll(`[role="radiogroup"][data-q="${side}"] [role="radio"]`).forEach((r) => {
        const on = Number(r.dataset.v) === this.s[side];
        r.setAttribute('aria-checked', String(on));
        r.classList.toggle('is-selected', on);
      });
      const group = el.querySelector(`[role="radiogroup"][data-q="${side}"]`);
      if (group) this.app.roving(group);
      this.app.renderFooter();
    }

    next() {
      if (this.s.step < 2) { this.s.step += 1; return; }
      this.s.step = 3;
      this.app.trackComplete();
    }

    back() { if (this.s.step > 0) this.s.step -= 1; }

    reset() {
      Object.assign(this.s, { step: 0, short: null, long: null, measure: { short: null, long: null } });
      this.help = null;
    }

    answers() { return { size: this.sizeKey() }; }
    resultKey() { return this.sizeKey() || ''; }
    query() { return { short: this.short?.n, long: this.long?.n }; }
    summary() {
      const m = this.s.measure;
      const measured = m.short != null || m.long != null
        ? ` / Measured: ${m.short != null ? inches(m.short) : '–'} × ${m.long != null ? inches(m.long) : '–'} (${m.short != null ? cmText(m.short) : '–'} × ${m.long != null ? cmText(m.long) : '–'} cm)`
        : '';
      return `Size Guide answers — Short side: ${this.short?.label || '–'} / Long side: ${this.long?.label || '–'}${measured}${this.help === 'round' ? ' / Round duct' : ''}`;
    }

    action(a, t) {
      const app = this.app;
      switch (a) {
        case 'help':
          app.go(() => { this.help = t.dataset.kind; });
          break;
        case 'again':
        case 'restart':
          app.go(() => this.reset(), 'back');
          break;
        case 'return':
          app.switchQuiz('vent_finder');
          break;
        case 'promo':
          app.start('vent_finder', { entry: 'handoff', prefill: { size: this.sizeKey() } });
          break;
        case 'unit': {
          this.s.unit = t.dataset.unit === 'cm' ? 'cm' : 'in';
          const box = t.closest('[data-fq-measure]');
          if (!box) break;
          const side = box.dataset.fqMeasure;
          box.querySelectorAll('[data-action="unit"]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.unit === this.s.unit)));
          const text = box.querySelector('[data-fq-text]');
          text.placeholder = this.s.unit === 'cm' ? 'e.g. 10.5' : 'e.g. 4 1/8';
          text.setAttribute('aria-label', `${side} side measurement in ${this.s.unit === 'cm' ? 'centimetres' : 'inches'}`);
          if (this.s.measure[side] != null) text.value = this.measureText(this.s.measure[side]);
          text.focus();
          break;
        }
        case 'add':
          app.addToCart(t);
          break;
        default:
      }
    }

    view() {
      if (this.help) return V.help(this.app, this.help, 'Measure again');
      return [this.viewPrep, this.viewShort, this.viewLong, this.viewResult][this.s.step].call(this);
    }

    viewPrep() {
      const img = this.app.data.config.images;
      return V.question({
        num: 1,
        name: 'Prep',
        title: 'First, pop off the <span class="fq-em">old vent.</span>',
        lead: 'Two quick measurements and you’ll know your exact size. Grab a tape measure — this takes about a minute.',
        extra: `<div class="fq-tips">${V.tip('do', 'Measure the duct — the hole in the wall or floor')}${V.tip('dont', 'Don’t measure the vent cover itself')}</div>`,
        answers: V.media(img.measure, 'fq-media--photo', 'Removing a vent cover to reach the duct opening')
      });
    }

    viewShort() {
      const d = this.app.data;
      const tiles = this.shorts.map((o) => V.tile({ v: o.n, label: o.label, n: o.n, selected: this.s.short === o.n })).join('');
      return V.question({
        num: 2,
        name: 'Short side',
        title: 'Measure the <span class="fq-em">short</span> side.',
        lead: 'Inside edge to inside edge, across the narrow side of the opening. Enter your number, or pick the range it falls in.',
        answers: `${V.media(d.config.images.duct_short, 'fq-media--duct', 'Duct opening with the short side highlighted', V.badge('Short side'))}
          ${this.measureHTML('short')}
          ${V.group('short', 'Short side measurement', tiles, 'fq-tiles')}
          <div class="fq-escapes">
            <button type="button" class="fq-escape" data-action="help" data-kind="none">None of these match</button>
            <button type="button" class="fq-escape" data-action="help" data-kind="round">My duct is round</button>
          </div>`
      });
    }

    viewLong() {
      const d = this.app.data;
      const tiles = this.longOptions().map((o) => V.tile({ v: o.n, label: o.label, n: o.n, selected: this.s.long === o.n })).join('');
      return V.question({
        num: 3,
        name: 'Long side',
        title: 'Now the <span class="fq-em">long</span> side.',
        lead: `Same method, end to end. We’ve narrowed the list to sizes that pair with your ${esc(this.short.n)}″ short side.`,
        answers: `${V.media(d.config.images.duct_long, 'fq-media--duct', 'Duct opening with the long side highlighted', V.badge('Long side'))}
          ${this.measureHTML('long')}
          ${V.group('long', 'Long side measurement', tiles, 'fq-tiles')}
          <div class="fq-escapes">
            <button type="button" class="fq-escape" data-action="help" data-kind="none">None of these match</button>
          </div>`
      });
    }

    viewResult() {
      const app = this.app;
      const d = app.data;
      const c = d.config;
      const key = this.sizeKey();
      // Every product in this size: wall, ceiling and floor vents (location wasn't asked), exhaust mounts last
      const products = d.productsWithSize(key).sort((a, b) => Number(a.exhaust) - Number(b.exhaust));
      const shopAll = (c.urls.shopAll || '').replace('{size}', encodeURIComponent(d.rawSizeFor(key)));

      const curated = products.length ? `<section class="fq-wrap">
        <div class="fq-section-head"><h2 class="fq-h2">Curated in <span class="fq-em">your size</span></h2><p>Every vent below is ${esc(fmtSizeLong(key))}.</p></div>
        <div class="fq-cards">${products.map((p) => V.card(app, this, p, key)).join('')}</div>
      </section>` : '';

      const promo = c.flags.showQuizPromo !== false && !this.returnTo ? `<section class="fq-wrap">${V.promo({
        eyebrow: 'Vent Finder',
        title: 'Not sure which <span class="fq-em">style?</span>',
        body: 'Five quick questions — location, finish, size, airflow, build — and we’ll hand you the one.',
        cta: V.btn('Take the Vent Finder', { variant: 'inverse', size: 'lg', action: 'promo' })
      })}</section>` : '';

      return `<section class="fq-hero fq-grid-red">
          <div class="fq-hero__inner">
            ${this.returnTo ? '<button type="button" class="fq-link fq-link--cream" data-action="return">← Back to Vent Finder</button>' : ''}
            <span class="fq-eyebrow">Your vent size is</span>
            <h1 class="fq-hero__size" id="fq-title" tabindex="-1">${esc(fmtSizeLong(key))}</h1>
            <p class="fq-hero__lead">Based on a duct opening of ${esc(this.short.label)} × ${esc(this.long.label)}. Order this size and it’ll drop right in.</p>
            <div class="fq-actions">
              ${shopAll ? `<a class="fq-btn fq-btn--inverse fq-btn--lg" href="${esc(shopAll)}" data-action="pdp">Shop all ${esc(fmtSizeLong(key))} vents</a>` : ''}
              ${V.btn('Measure again', { variant: 'outline-cream', action: 'restart' })}
            </div>
          </div>
        </section>
        ${curated}${promo}
        <div class="fq-end">${V.btn('Measure again', { variant: 'ghost', action: 'restart' })}</div>`;
    }
  }

  /* ------------------------------------------------------------------ app */

  class QuizApp {
    constructor(root, { mode, scroller, host }) {
      this.root = root;
      this.el = root.querySelector('[data-fq-app]');
      this.mode = mode;
      this.scroller = scroller;
      this.host = host;
      this.data = new QuizData(root);
      this.cartCount = this.data.config.cartCount || 0;
      this.cartChanged = false;
      this.focusKey = null;
      this.lastStepKey = null;

      this.el.addEventListener('click', (e) => this.onClick(e));
      this.el.addEventListener('change', (e) => this.onChange(e));
      this.el.addEventListener('input', (e) => this.quiz?.onInput?.(e));
      (mode === 'overlay' ? scroller : document).addEventListener('keydown', (e) => this.onKey(e));
      this.refreshCount();
    }

    start(quiz, { entry = 'direct', prefill = {}, returnTo = null } = {}) {
      clearTimeout(this.leaveTimer);
      this.leaving = false;
      this.quiz = quiz === 'size_guide' ? new SizeGuide(this, prefill, returnTo) : new VentFinder(this, prefill);
      this.lastStepKey = null;
      track('start', this.quiz.id, entry);
      this.render();
    }

    switchQuiz(to) {
      const from = this.quiz;
      if (to === 'size_guide' && from.id === 'vent_finder') {
        this.start('size_guide', { entry: 'handoff', returnTo: { quiz: 'vent_finder', state: from.snapshot() } });
      } else if (to === 'vent_finder' && from.id === 'size_guide' && from.returnTo) {
        const size = from.s.step === 3 ? from.sizeKey() : from.returnTo.state.size;
        this.start('vent_finder', { entry: 'handoff', prefill: { ...from.returnTo.state, size, step: 2 } });
      } else {
        this.start(to, { entry: 'handoff' });
      }
    }

    /* rendering */

    render({ stepChanged = true, enter = 'fwd' } = {}) {
      const q = this.quiz;
      let shell = this.el.querySelector('.fq-shell');
      if (!shell || shell.dataset.quiz !== q.id) {
        this.el.innerHTML = `<div class="fq-shell" data-quiz="${q.id}">${this.headerHTML()}<main class="fq-main" id="fq-main"></main></div>`;
        shell = this.el.querySelector('.fq-shell');
      }
      this.syncProgress(shell);
      const main = shell.querySelector('.fq-main');
      main.className = 'fq-main';
      main.innerHTML = q.view();
      if (stepChanged && enter && !reducedMotion()) {
        main.classList.add('fq-enter');
        if (enter === 'back') main.classList.add('fq-enter--back');
      }
      this.syncFooter(shell);
      this.el.querySelectorAll('[role="radiogroup"]').forEach((g) => this.roving(g));

      const stepKey = `${q.id}:${q.stepId()}`;
      if (stepChanged) {
        if (this.scroller === window) window.scrollTo({ top: 0 });
        else this.scroller.scrollTo({ top: 0 });
        this.el.querySelector('#fq-title')?.focus({ preventScroll: true });
        if (stepKey !== this.lastStepKey) track('stepView', q.stepId());
        this.lastStepKey = stepKey;
      }
      if (this.focusKey) {
        this.el.querySelector(`[data-focus="${this.focusKey}"]`)?.focus({ preventScroll: true });
        this.focusKey = null;
      }
      this.syncUrl();
    }

    headerHTML() {
      const q = this.quiz;
      const c = this.data.config;
      const other = q.id === 'vent_finder' ? ['Size guide', 'goto-size_guide'] : ['Vent Finder', 'goto-vent_finder'];
      const logo = c.logo
        ? `<img class="fq-logo" src="${esc(c.logo)}" alt="${esc(c.shopName || 'Home')}">`
        : `<span class="fq-logo-text">${esc(c.shopName || 'Home')}</span>`;
      return `<header class="fq-header">
        <div class="fq-header__row">
          <div class="fq-brand">
            <a class="fq-brand__home" href="${esc(c.urls.home || '/')}" data-action="home" aria-label="${esc(c.shopName || '')} home page">${logo}</a>
            <span class="fq-divider" aria-hidden="true"></span>
            <span class="fq-eyebrow">${esc(q.label)}</span>
          </div>
          <div class="fq-header__right">
            <button type="button" class="fq-header__link" data-action="${other[1]}">${other[0]}</button>
            <a class="fq-cart" href="${esc(c.urls.cart || '/cart')}" data-action="cart">${ICON.bag}<span><span class="fq-cart__word">Cart </span>(<span data-fq-cart-count>${this.cartCount}</span>)</span></a>
            <button type="button" class="fq-exit" data-action="exit" aria-label="Exit ${esc(q.label)}">${ICON.close}</button>
          </div>
        </div>
      </header>`;
    }

    // Progress row persists between steps so the red fill animates instead of re-drawing
    syncProgress(shell) {
      const q = this.quiz;
      const header = shell.querySelector('.fq-header');
      let prog = header.querySelector('.fq-progress');
      if (!q.isQuestion()) { prog?.remove(); return; }
      if (!prog) {
        header.insertAdjacentHTML('beforeend', `<div class="fq-progress">
          <span class="fq-progress__label"></span>
          <div class="fq-progress__bar" role="progressbar" aria-label="Quiz progress" aria-valuemin="1" aria-valuemax="${q.total}">
            ${'<span class="fq-progress__seg"></span>'.repeat(q.total)}
          </div>
        </div>`);
        prog = header.querySelector('.fq-progress');
      }
      prog.querySelector('.fq-progress__label').textContent = `Step ${pad2(q.stepNum())} / ${pad2(q.total)}`;
      const bar = prog.querySelector('.fq-progress__bar');
      bar.setAttribute('aria-valuenow', q.stepNum());
      bar.querySelectorAll('.fq-progress__seg').forEach((seg, i) => seg.classList.toggle('is-on', i < q.stepNum()));
    }

    syncFooter(shell) {
      let f = shell.querySelector('[data-fq-footer]');
      if (!this.quiz.isQuestion()) { f?.remove(); return; }
      if (!f) {
        shell.insertAdjacentHTML('beforeend', '<div class="fq-footer" data-fq-footer></div>');
        f = shell.querySelector('[data-fq-footer]');
      }
      f.innerHTML = this.footerHTML();
    }

    footerHTML() {
      const q = this.quiz;
      return `<div class="fq-footer__row">
        ${V.btn('Back', { variant: 'ghost', size: 'lg', action: 'back', disabled: !q.canBack() })}
        <span class="fq-trail" aria-live="polite">${esc(q.trail())}</span>
        ${q.canContinue() ? V.btn(esc(q.ctaLabel()), { variant: 'primary', size: 'lg', action: 'continue' }) : '<span class="fq-footer__spacer" aria-hidden="true"></span>'}
      </div>`;
    }

    renderFooter() {
      const f = this.el.querySelector('[data-fq-footer]');
      if (f) f.innerHTML = this.footerHTML();
    }

    roving(group) {
      const items = [...group.querySelectorAll('[role="radio"]')];
      const sel = items.find((r) => r.getAttribute('aria-checked') === 'true') || items[0];
      items.forEach((r) => { r.tabIndex = r === sel ? 0 : -1; });
    }

    /* events */

    onClick(e) {
      const t = e.target.closest('[data-action]');
      if (!t || !this.el.contains(t) || t.disabled) return;
      const a = t.dataset.action;
      switch (a) {
        case 'home': this.trackExit('logo'); return; // let the link navigate
        case 'cart': this.trackExit('cart'); return;
        case 'pdp': this.trackExit('product_link'); return;
        case 'email': return;
        case 'exit': e.preventDefault(); this.exit('close_button'); return;
        case 'continue': this.continue(); return;
        case 'back': this.go(() => this.quiz.back(), 'back'); return;
        case 'pick': this.pick(t, { advance: true }); return;
        case 'goto-size_guide': this.switchQuiz('size_guide'); return;
        case 'goto-vent_finder': this.switchQuiz('vent_finder'); return;
        case 'chat': this.openChat(); return;
        default: this.quiz.action(a, t, e);
      }
    }

    onChange(e) {
      const sel = e.target.closest('[data-fq-select]');
      if (!sel) return;
      this.quiz.set(sel.dataset.fqSelect, sel.value || null);
      if (sel.value) this.continue();
      else this.renderFooter();
    }

    onKey(e) {
      if (!this.quiz || (this.mode === 'overlay' && !this.host.dialog.open)) return;
      const radio = e.target.closest?.('[role="radio"]');
      const arrows = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 };
      if (radio && arrows[e.key]) {
        e.preventDefault();
        const items = [...radio.closest('[role="radiogroup"]').querySelectorAll('[role="radio"]')];
        const next = items[(items.indexOf(radio) + arrows[e.key] + items.length) % items.length];
        next.focus();
        this.pick(next);
        return;
      }
      if (e.key !== 'Enter' || !this.quiz.isQuestion()) return;
      if (radio) {
        e.preventDefault();
        if (radio.getAttribute('aria-checked') !== 'true') this.pick(radio);
        if (this.quiz.canContinue()) this.continue();
        return;
      }
      if (e.target.matches?.('[data-fq-text]')) {
        if (this.quiz.canContinue()) { e.preventDefault(); this.continue(); }
        return;
      }
      if (['BUTTON', 'A', 'INPUT', 'SELECT', 'TEXTAREA'].includes(e.target.tagName)) return;
      if (this.quiz.canContinue()) { e.preventDefault(); this.continue(); }
    }

    pick(el, { advance = false } = {}) {
      if (this.leaving) return;
      const group = el.closest('[role="radiogroup"]');
      const v = el.dataset.v;
      this.quiz.set(group.dataset.q, v);
      group.querySelectorAll('[role="radio"]').forEach((r) => {
        const on = r.dataset.v === v;
        r.setAttribute('aria-checked', String(on));
        r.classList.toggle('is-selected', on);
        r.tabIndex = on ? 0 : -1;
      });
      // The transition starts on the same click: no pause before leaving the step
      if (advance && this.quiz.isQuestion() && this.quiz.canContinue()) this.continue();
      else this.renderFooter();
    }

    continue() {
      if (!this.quiz.canContinue()) return;
      this.go(() => this.quiz.next());
    }

    // Animate the current step out, apply the change, animate the next step in
    go(change, dir = 'fwd') {
      if (this.leaving) return;
      const main = this.el.querySelector('.fq-main');
      if (!main || reducedMotion()) { change(); this.render({ enter: false }); return; }
      this.leaving = true;
      main.classList.remove('fq-enter', 'fq-enter--back');
      main.classList.add('fq-leave');
      if (dir === 'back') main.classList.add('fq-leave--back');
      this.leaveTimer = setTimeout(() => {
        this.leaving = false;
        change();
        this.render({ enter: dir });
      }, LEAVE_MS);
    }

    /* analytics */

    trackComplete() { track('complete', this.quiz.answers(), this.quiz.resultKey()); }
    trackExit(method) { track('exit', this.quiz?.stepId(), method); }

    /* exit */

    exit(method) {
      if (this.mode === 'overlay') { this.host.close(method); return; }
      this.trackExit(method);
      let sameSite = false;
      try { sameSite = !!document.referrer && new URL(document.referrer).origin === location.origin; } catch (e) { /* ignore */ }
      if (sameSite && history.length > 1) history.back();
      else location.href = this.data.config.urls.home || '/';
    }

    /* URL */

    syncUrl() {
      const u = new URL(location.href);
      PARAMS.forEach((k) => u.searchParams.delete(k));
      const q = this.quiz;
      const isPageDefault = this.mode === 'standalone' && q.id === this.data.config.defaultQuiz;
      if (!isPageDefault) u.searchParams.set('quiz', SLUG[q.id]);
      Object.entries(q.query()).forEach(([k, v]) => { if (v != null && v !== '') u.searchParams.set(k, v); });
      history.replaceState(history.state, '', u);
    }

    /* cart */

    async addToCart(btn) {
      const c = this.data.config;
      const id = Number(btn.dataset.variant);
      const qty = Number(btn.dataset.qty) || 1;
      const label = btn.innerHTML;
      btn.disabled = true;
      btn.textContent = 'Adding…';
      btn.parentElement.querySelector('.fq-error')?.remove();
      try {
        const res = await fetch(`${c.urls.cartAdd || '/cart/add'}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{ id, quantity: qty, properties: { _quiz: this.quiz.id, _quiz_result: this.quiz.resultKey() } }],
            sections: 'cart-icon-bubble',
            sections_url: location.pathname
          })
        });
        const json = await res.json();
        if (!res.ok) throw new Error(json.description || json.message || 'This item couldn’t be added.');
        this.cartChanged = true;
        this.quiz.added[id] = true;
        updateCartBubble(json.sections);
        if (this.mode === 'overlay') watchForCart(this.host.dialog);
        track('addToCart', btn.dataset.name, (Number(btn.dataset.price) * qty) / 100);
        await this.refreshCount();
        this.focusKey = `add-${id}`;
        this.render({ stepChanged: false });
      } catch (err) {
        btn.disabled = false;
        btn.innerHTML = label;
        btn.insertAdjacentHTML('afterend', `<p class="fq-error" role="alert">${esc(err.message)} Try again.</p>`);
      }
    }

    async refreshCount() {
      try {
        const res = await fetch(`${this.data.config.urls.cart || '/cart'}.js`, { headers: { Accept: 'application/json' } });
        const cart = await res.json();
        this.cartCount = cart.item_count;
        this.el.querySelectorAll('[data-fq-cart-count]').forEach((n) => { n.textContent = cart.item_count; });
      } catch (e) { /* keep the last known count */ }
    }

    /* chat */

    openChat() {
      const w = window;
      const tryOpen = () => {
        try {
          if (typeof w.FittesOpenChat === 'function') { w.FittesOpenChat(); return true; }
          if (w.GorgiasChat?.open) { w.GorgiasChat.open(); return true; }
          if (w.tidioChatApi?.open) { w.tidioChatApi.open(); return true; }
          if (typeof w.Intercom === 'function') { w.Intercom('show'); return true; }
          if (typeof w.zE === 'function') { w.zE('messenger', 'open'); return true; }
          if (w.$crisp) { w.$crisp.push(['do', 'chat:open']); return true; }
        } catch (e) { /* fall through to the link */ }
        return false;
      };
      // A chat widget can't sit above a modal dialog, so close the quiz first
      if (this.mode === 'overlay') this.host.close('chat');
      if (!tryOpen()) {
        const url = this.data.config.urls.chat || this.data.config.urls.contact;
        if (url) location.href = url;
      }
    }
  }

  /* ------------------------------------------------------------------ theme cart helpers (Dawn) */

  function sectionInner(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return (doc.querySelector('.shopify-section') || doc.body).innerHTML;
  }

  function updateCartBubble(sections) {
    const html = sections?.['cart-icon-bubble'];
    const target = document.getElementById('cart-icon-bubble');
    if (html && target) target.innerHTML = sectionInner(html);
  }

    async function refreshCartDrawer() {
    // PickyStory is the live cart: it watches /cart/add itself and refreshes/opens its own drawer.
    // Dawn's renderContents() would rewrite #cart-icon-bubble (wiping PickyStory's click target)
    // and open Dawn's hidden drawer, leaving the header cart icon dead until reload.
    if (window.pickystory || document.querySelector('[data-picky-cart-button-link]')) return;
    try {
      const root = window.Shopify?.routes?.root || '/';
      const res = await fetch(`${root}?sections=cart-drawer,cart-icon-bubble`);
      const sections = await res.json();
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.renderContents === 'function') {
        drawer.classList.remove('is-empty');
        drawer.renderContents({ sections }); // Dawn: updates drawer + bubble, then opens the drawer
      } else {
        updateCartBubble(sections);
      }
    } catch (e) { /* the cart page is still correct */ }
  }

  /* Cart drawers sit above the quiz overlay. Each cart's OUTER container is raised as one unit,
     so its own overlay and panel keep the app's order inside it (Amplify/PickyStory puts the grey
     overlay and the cart panel side by side in .picky-cart). Nothing else on the page is touched.
     Add another cart app's root selector here if you ever switch apps. */
  const QUIZ_Z = 2147483000;
  const CART_ROOTS = ['.picky-cart', '#picky-cart-sidebars-container', 'cart-drawer'];
  const raised = new Map(); // element -> original inline { z, position }
  function raiseCarts() {
    document.querySelectorAll(CART_ROOTS.join(',')).forEach((el) => {
      if (raised.has(el)) return;
      raised.set(el, { z: el.style.getPropertyValue('z-index'), position: el.style.getPropertyValue('position') });
      // z-index only applies to positioned elements; fixed/absolute roots keep their own positioning
      if (!['fixed', 'absolute', 'relative', 'sticky'].includes(getComputedStyle(el).position)) el.style.setProperty('position', 'relative');
      el.style.setProperty('z-index', '2147483647', 'important');
    });
  }
  function watchForCart(dialog) {
    let n = 0;
    const tick = () => {
      if (!dialog.open) return;
      raiseCarts(); // catches cart containers an app only creates after the first add
      if (++n < 20) setTimeout(tick, 100);
    };
    tick();
  }
  function restoreCarts() {
    raised.forEach((orig, el) => {
      if (orig.z) el.style.setProperty('z-index', orig.z); else el.style.removeProperty('z-index');
      if (orig.position) el.style.setProperty('position', orig.position); else el.style.removeProperty('position');
    });
    raised.clear();
  }
  // Open = Amplify adds an "isVisible" class to .picky-cart; Dawn's drawer gets .active
  const cartIsOpen = () => [...raised.keys()].some((el) => /isVisible/.test(el.className) || el.classList.contains('active'));

  function closeThemeMenus() {
    document.querySelectorAll('header details[open], .header-wrapper details[open], menu-drawer details[open]').forEach((d) => {
      d.removeAttribute('open');
      d.classList.remove('menu-opening');
    });
    document.body.classList.remove('overflow-hidden', 'overflow-hidden-mobile', 'overflow-hidden-tablet', 'overflow-hidden-desktop');
  }

  function fromQuery(p) {
    const pre = { fromQuery: true };
    const map = { loc: 'location', finish: 'finish', size: 'size', air: 'airflow', mat: 'material', short: 'short', long: 'long' };
    Object.entries(map).forEach(([k, key]) => { if (p.get(k)) pre[key] = p.get(k); });
    return pre;
  }

  /* ------------------------------------------------------------------ overlay */

  const Overlay = {
    dialog: null,
    app: null,
    pending: null,
    opener: null,
    paths: {},

    init() {
      this.dialog = document.querySelector('[data-fq-dialog]');
      if (!this.dialog) return;
      this.paths = {
        vent_finder: this.dialog.dataset.ventFinderUrl || '/pages/vent-finder',
        size_guide: this.dialog.dataset.sizeGuideUrl || '/pages/size-guide'
      };
      this.dialog.addEventListener('cancel', (e) => { e.preventDefault(); this.close('escape'); });
      // Non-modal dialogs don't get Esc for free. If a cart drawer is open on top, Esc is left to the cart.
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.dialog.open && !cartIsOpen()) this.close('escape');
      });
      window.addEventListener('popstate', () => { if (this.dialog.open) this.close('browser_back', { fromPop: true }); });
      document.addEventListener('click', (e) => this.intercept(e));

      const p = new URLSearchParams(location.search);
      const quiz = KEY[p.get('quiz')];
      if (quiz) this.open(quiz, { entry: 'link', prefill: fromQuery(p), replace: true });
    },

    quizFromLink(a) {
      if (a.tagName !== 'A' || !a.href) return null;
      let path;
      try {
        const u = new URL(a.href, location.href);
        if (u.origin !== location.origin) return null;
        path = u.pathname.replace(/\/$/, '');
      } catch (e) {
        return null;
      }
      return Object.keys(this.paths).find((k) => path.endsWith(this.paths[k].replace(/\/$/, ''))) || null;
    },

    intercept(e) {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const t = e.target.closest('[data-fittes-quiz], a[href]');
      if (!t || this.dialog.contains(t) || t.target === '_blank') return;
      const quiz = KEY[t.dataset.fittesQuiz] || this.quizFromLink(t);
      if (!quiz) return;
      e.preventDefault();
      let prefill = {};
      try { prefill = JSON.parse(t.dataset.fqPrefill || '{}'); } catch (err) { /* ignore */ }
      const inHeader = t.closest('header, .header-wrapper, #shopify-section-header, .section-header, menu-drawer, header-drawer');
      this.open(quiz, { entry: t.dataset.fqEntry || (inHeader ? 'menu' : 'link'), prefill });
    },

    async load(quiz) {
      if (this.app) return this.app;
      if (!this.pending) {
        this.pending = (async () => {
          const res = await fetch(this.paths[quiz], { headers: { Accept: 'text/html' } });
          if (!res.ok) throw new Error(`Quiz page returned ${res.status}`);
          const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
          const src = doc.querySelector('[data-fq-root]');
          if (!src) throw new Error('Quiz markup not found on the quiz page');
          const root = document.importNode(src, true);
          root.removeAttribute('data-fq-standalone');
          this.dialog.replaceChildren(root);
          this.app = new QuizApp(root, { mode: 'overlay', scroller: this.dialog, host: this });
          return this.app;
        })();
      }
      try {
        return await this.pending;
      } catch (err) {
        this.pending = null;
        throw err;
      }
    },

    async open(quiz, { entry = 'link', prefill = {}, replace = false } = {}) {
      if (this.dialog.open) return;
      this.opener = document.activeElement;
      closeThemeMenus();
      if (!this.app) this.dialog.innerHTML = '<div class="fq"><div class="fq-dialog__loading"><span class="fq-spinner" aria-label="Loading"></span></div></div>';
      this.dialog.show(); // not showModal(): the top layer would hide the cart drawer behind the quiz
      raiseCarts();
      try {
        const app = await this.load(quiz);
        if (replace) history.replaceState({ fq: 'deeplink' }, '', location.href);
        else history.pushState({ fq: true }, '', location.href);
        app.start(quiz, { entry, prefill });
      } catch (err) {
        console.error('[FittesQuiz]', err);
        this.dialog.close();
        location.href = this.paths[quiz]; // fall back to the standalone page
      }
    },

    close(method = 'close_button', { fromPop = false } = {}) {
      if (!this.dialog.open) return;
      this.app?.trackExit(method);
      this.dialog.close();
      restoreCarts();
      if (!fromPop) {
        if (history.state?.fq === true) {
          history.back();
        } else {
          const u = new URL(location.href);
          PARAMS.forEach((k) => u.searchParams.delete(k));
          history.replaceState(null, '', u);
        }
      }
      if (this.app?.cartChanged) {
        this.app.cartChanged = false;
        refreshCartDrawer();
      }
      this.opener?.focus?.({ preventScroll: true });
    }
  };

  /* ------------------------------------------------------------------ boot */

  function initStandalone() {
    const root = document.querySelector('[data-fq-root][data-fq-standalone]');
    if (!root || root.closest('dialog')) return false;
    const app = new QuizApp(root, { mode: 'standalone', scroller: window, host: null });
    const p = new URLSearchParams(location.search);
    let entry = 'direct';
    try { if (document.referrer && new URL(document.referrer).origin === location.origin) entry = 'link'; } catch (e) { /* ignore */ }
    app.start(KEY[p.get('quiz')] || app.data.config.defaultQuiz, { entry, prefill: fromQuery(p) });
    return true;
  }

  function boot() {
    if (!initStandalone()) Overlay.init();
  }

  window.FittesQuiz = {
    open: (quiz, opts) => (Overlay.dialog ? Overlay.open(KEY[quiz] || 'vent_finder', opts) : null),
    close: () => Overlay.close('api')
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();